/**
 * Célébrations à paliers (livré en 0.28.0, E§1.4).
 *
 * Deux effets, deux intensités — et la retenue fait partie du contrat :
 *
 * - **`burst`** : une gerbe de particules depuis un point. C'est la célébration
 *   ORDINAIRE, jouée à chaque jalon. Courte, locale, jamais envahissante.
 * - **`confetti`** : la pluie plein écran. L'appelant décide de sa rareté (une fois
 *   par session, E§1.4 : « la rareté fait l'effet ») — ce module ne compte pas les
 *   sessions, il obéit.
 *
 * Même architecture que le vol des pièces : espace écran, une texture partagée,
 * réserve d'objets, zéro allocation dans la boucle de rendu (E§3). Les couleurs
 * tirent dans une petite palette fixe — cyan des gemmes, or des pièces — pour que
 * chaque célébration rappelle ce qu'elle récompense.
 */

import { Graphics, Sprite, type Container, type Renderer, type Texture } from 'pixi.js';

import { JUICE } from '@/config/juice';
import type { TweenRunner } from '@/core/tween';
import { Pool } from './pool';
import type { ScreenPoint } from './coinFlight';

/** Cyan gemme, or pièce, blanc d'éclat — la fête reste dans l'univers du jeu. */
const PALETTE = [0x59e0e8, 0x8ff0f5, 0xf7b23b, 0xffe9a8, 0xffffff] as const;

/** Trace de la dernière célébration — lue par les sondes `?debug`, jamais par le jeu. */
export interface CelebrationTrace {
  readonly kind: 'burst' | 'confetti';
  readonly particles: number;
  readonly at: number;
}

export class Celebrations {
  private readonly pool: Pool<Sprite>;
  private readonly texture: Texture;
  private last: CelebrationTrace | null = null;

  constructor(
    renderer: Renderer,
    private readonly layer: Container,
    private readonly tweens: TweenRunner,
  ) {
    // Un petit rectangle blanc : teinté par particule, il fait confetti comme étincelle.
    const g = new Graphics().rect(0, 0, 7, 11).fill({ color: 0xffffff });
    this.texture = renderer.generateTexture(g);
    g.destroy();

    this.pool = new Pool<Sprite>({
      create: () => {
        const sprite = new Sprite(this.texture);
        sprite.anchor.set(0.5);
        sprite.visible = false;
        this.layer.addChild(sprite);
        return sprite;
      },
      reset: (sprite) => {
        sprite.visible = false;
        sprite.alpha = 1;
        sprite.rotation = 0;
        sprite.scale.set(1);
      },
      initialSize: 16,
      // Confettis (90) + une gerbe (26) peuvent se superposer une fois par session.
      maxSize: 128,
    });
  }

  /** Gerbe radiale depuis un point écran — la célébration de chaque jalon. */
  burst(at: ScreenPoint, now: number): void {
    const { burstParticles, burstMs } = JUICE.milestone;
    let launched = 0;

    for (let i = 0; i < burstParticles; i++) {
      const sprite = this.pool.acquire();
      if (sprite === null) break;
      launched++;

      const angle = (Math.PI * 2 * i) / burstParticles + Math.random() * 0.5;
      const speed = 90 + Math.random() * 130;
      const tint = PALETTE[i % PALETTE.length] ?? 0xffffff;
      sprite.tint = tint;
      sprite.visible = true;
      sprite.position.set(at.x, at.y);
      const spin = (Math.random() - 0.5) * 8;

      this.tweens.add(
        {
          from: 0,
          to: 1,
          durationMs: burstMs,
          easing: 'out',
          onUpdate: (t) => {
            if (sprite.destroyed) return;
            // Trajectoire balistique simple : élan radial, puis la gravité reprend.
            sprite.position.set(
              at.x + Math.cos(angle) * speed * t,
              at.y + Math.sin(angle) * speed * t + 190 * t * t,
            );
            sprite.rotation = spin * t;
            sprite.alpha = t < 0.6 ? 1 : 1 - (t - 0.6) / 0.4;
          },
          onComplete: () => {
            this.pool.release(sprite);
          },
        },
        now,
      );
    }

    this.last = { kind: 'burst', particles: launched, at: now };
  }

  /** Pluie plein écran. La RARETÉ appartient à l'appelant — ici on ne fait que pleuvoir. */
  confetti(width: number, height: number, now: number): void {
    const { confettiParticles, confettiMs } = JUICE.milestone;
    let launched = 0;

    for (let i = 0; i < confettiParticles; i++) {
      const sprite = this.pool.acquire();
      if (sprite === null) break;
      launched++;

      const x = Math.random() * width;
      const sway = 30 + Math.random() * 60;
      const phase = Math.random() * Math.PI * 2;
      const fall = height + 40;
      const tint = PALETTE[i % PALETTE.length] ?? 0xffffff;
      sprite.tint = tint;
      sprite.visible = true;
      sprite.position.set(x, -20);
      const spin = (Math.random() - 0.5) * 12;

      this.tweens.add(
        {
          from: 0,
          to: 1,
          durationMs: confettiMs,
          // Chaque flocon part avec son retard : une pluie, pas un rideau.
          delayMs: Math.random() * confettiMs * 0.35,
          easing: 'in',
          onUpdate: (t) => {
            if (sprite.destroyed) return;
            sprite.position.set(x + Math.sin(phase + t * 6) * sway, -20 + fall * t);
            sprite.rotation = spin * t;
            sprite.alpha = t < 0.8 ? 1 : 1 - (t - 0.8) / 0.2;
          },
          onComplete: () => {
            this.pool.release(sprite);
          },
        },
        now,
      );
    }

    this.last = { kind: 'confetti', particles: launched, at: now };
  }

  /** Particules actuellement à l'écran — lu par les sondes et les tests. */
  get activeParticles(): number {
    return this.pool.inUse;
  }

  /** Dernière célébration jouée — réservé aux sondes `?debug`. */
  get lastCelebration(): CelebrationTrace | null {
    return this.last;
  }
}
